import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Minus, Plus, RefreshCw } from 'lucide-react';
import {
  formatPercent,
  TV_AUTOSCROLL_DEFAULT,
  TV_AUTOSCROLL_LABELS,
  TV_AUTOSCROLL_OPTIONS,
  TV_DASHBOARD_REFRESH_CHECK_MS,
  TV_DASHBOARD_REFRESH_MS,
  TV_SCREEN_INFO,
  TV_SCREENS,
  TV_ZOOM_DEFAULT_PCT,
  TV_ZOOM_MAX_PCT,
  TV_ZOOM_MIN_PCT,
  TV_ZOOM_STEP_PCT,
  type TvAutoscroll,
  type TvDashboardResponse,
  type TvDashboardSettings,
  type TvInternalResponse,
  type TvProjectsResponse,
  type TvScreen,
  type TvScreenToken,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { Alert, Button, CardBody, CollapsibleCard, Label, Select, Skeleton } from '@/components/ui';
import { PageHeader } from '@/components/domain';
import { TvSellersSettings } from '@/components/TvSellersSettings';
import { TvUtilizationSettings } from '@/components/TvUtilizationSettings';
import { TvWelcomeSettings } from '@/components/TvWelcomeSettings';
import { TvDashboardView, TvPreview } from '@/pages/TvDashboard';
import { TvInternalView } from '@/pages/TvInternal';
import { TvProjectsView } from '@/pages/TvProjects';

/**
 * /admin/tv-dashboard - how the screens on /dashboard/tv look and what they measure against: the
 * zoom and the autoscroll, the normtid and targets behind the utilization cards (see
 * TvUtilizationSettings) and the sellers on "Salg pr. sælger" (see TvSellersSettings). The
 * revenue budget behind the goals is on /budget since 26-10-2026.
 *
 * The preview draws a screen's own components with the numbers it shows now, so what is seen here
 * is what that screen will show at the chosen zoom and autoscroll. Which of the three is chosen in
 * its dropdown, here only - each TV shows its own screen. "Kopier URL" copies the chosen screen's
 * address with its token, for the computer behind the TV. The display settings are stored as
 * `DASHBOARD_TV_ZOOM` and `DASHBOARD_TV_AUTOSCROLL` in `codepass_settings` - see the /tv-dashboard
 * routes in apps/api/src/routes/admin.ts and config/app-settings.ts.
 */

const SETTINGS_KEY = ['admin', 'tv-dashboard', 'settings'] as const;
const PREVIEW_KEY = ['admin', 'tv-dashboard', 'preview'] as const;
/* Not under 'preview': "Opdater skærmen nu" refetches these, see `refresh`. */
const PROJECTS_PREVIEW_KEY = ['admin', 'tv-dashboard', 'preview-projects'] as const;
const INTERNAL_PREVIEW_KEY = ['admin', 'tv-dashboard', 'preview-internal'] as const;

/** How long "Opdater skærmen nu" takes to reach the screen, in whole seconds. */
const REFRESH_REACH_SECONDS = Math.round(TV_DASHBOARD_REFRESH_CHECK_MS / 1000);

const DEFAULT_SETTINGS: TvDashboardSettings = {
  zoom_pct: TV_ZOOM_DEFAULT_PCT,
  autoscroll: TV_AUTOSCROLL_DEFAULT,
};

export function AdminTvDashboardPage() {
  const queryClient = useQueryClient();
  /* 'Se TV-dashboard' uden 'Ændr': visningen og forhaandsvisningen til at laese. */
  const canEdit = useAuth().can('tv_dashboard.manage');
  const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  const settings = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => api.get<TvDashboardSettings>('/tv-dashboard/settings'),
  });

  /** The screen in the preview. Only on this page - it changes nothing on the TVs. */
  const [screen, setScreen] = useState<TvScreen>('growth');

  const preview = useQuery({
    queryKey: PREVIEW_KEY,
    queryFn: () => api.get<TvDashboardResponse>('/tv-dashboard/preview'),
    // The API caches each source for at least this long, so fetching more often gives nothing new.
    staleTime: TV_DASHBOARD_REFRESH_MS,
  });

  const projectsPreview = useQuery({
    queryKey: PROJECTS_PREVIEW_KEY,
    queryFn: () => api.get<TvProjectsResponse>('/tv-dashboard/preview/projects'),
    staleTime: TV_DASHBOARD_REFRESH_MS,
    enabled: screen === 'projects',
  });

  const internalPreview = useQuery({
    queryKey: INTERNAL_PREVIEW_KEY,
    queryFn: () => api.get<TvInternalResponse>('/tv-dashboard/preview/internal'),
    staleTime: TV_DASHBOARD_REFRESH_MS,
    enabled: screen === 'internal',
  });

  /*
   * "Kopier URL": the chosen screen's address with its token, on this site's own origin. The token
   * is fetched on the click and not kept in the page - it is a secret, and the call is audited.
   */
  const copyUrl = useMutation({
    mutationFn: async (chosen: TvScreen) => {
      const { token } = await api.get<TvScreenToken>(`/tv-dashboard/screens/${chosen}/url`);
      const info = TV_SCREEN_INFO[chosen];
      if (!token) {
        throw new Error(
          `${info.label} har intet token endnu. Sæt ${info.token_setting} på /admin/integrationer.`,
        );
      }
      const url = `${window.location.origin}${info.path}?token=${encodeURIComponent(token)}`;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        throw new Error('Browseren tillod ikke at kopiere. Prøv igen fra fanen, der har fokus.');
      }
      return info.label;
    },
    onMutate: () => setMessage(null),
    onSuccess: (label) =>
      setMessage({
        tone: 'info',
        text: `Adressen til ${label} er kopieret med tokenet. Sæt den ind i browseren på computeren ved TV'et.`,
      }),
    onError: (cause) =>
      setMessage({
        tone: 'error',
        text: cause instanceof Error ? cause.message : 'Kunne ikke kopiere adressen.',
      }),
  });

  /** The settings being tried. Null until something is touched; until then the saved ones show. */
  const [draft, setDraft] = useState<TvDashboardSettings | null>(null);
  const saved = settings.data ?? DEFAULT_SETTINGS;
  const current = draft ?? saved;
  const dirty =
    draft !== null && (draft.zoom_pct !== saved.zoom_pct || draft.autoscroll !== saved.autoscroll);

  const edit = (change: Partial<TvDashboardSettings>): void => setDraft({ ...current, ...change });
  const clamp = (value: number): number =>
    Math.min(TV_ZOOM_MAX_PCT, Math.max(TV_ZOOM_MIN_PCT, value));

  /* Optimistic: the page shows the new values at once and rolls back if the save fails. */
  const save = useMutation({
    mutationFn: (next: TvDashboardSettings) =>
      api.put<TvDashboardSettings>('/tv-dashboard/settings', next),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: SETTINGS_KEY });
      const previous = queryClient.getQueryData<TvDashboardSettings>(SETTINGS_KEY);
      queryClient.setQueryData<TvDashboardSettings>(SETTINGS_KEY, next);
      setDraft(null);
      setMessage(null);
      return { previous };
    },
    onError: (cause, next, context) => {
      queryClient.setQueryData(SETTINGS_KEY, context?.previous);
      // The attempted values stay in the form, so they can be saved again without redoing them.
      setDraft(next);
      setMessage({
        tone: 'error',
        text: cause instanceof ApiError ? cause.message : 'Kunne ikke gemme visningen.',
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(SETTINGS_KEY, result);
      setMessage({
        tone: 'info',
        text: `Visningen er gemt: zoom ${formatPercent(result.zoom_pct)}, autoscroll ${TV_AUTOSCROLL_LABELS[result.autoscroll].toLowerCase()}. Skærmen viser den ved sin næste opdatering, senest om fem minutter — eller med det samme med "Opdater skærmen nu".`,
      });
    },
  });

  /*
   * "Opdater skærmen nu": the API reads every source anew and tells the screen, which fetches
   * within `TV_DASHBOARD_REFRESH_CHECK_MS`. The answer is the new numbers, so the preview has them
   * at once.
   */
  const refresh = useMutation({
    mutationFn: () => api.post<TvDashboardResponse>('/tv-dashboard/refresh'),
    onMutate: () => setMessage(null),
    onSuccess: (result) => {
      queryClient.setQueryData(PREVIEW_KEY, result);
      /* The budget and utilization cards read the caches that were just cleared. */
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tv-dashboard'],
        predicate: (query) => query.queryKey[2] !== 'preview',
      });
      setMessage({
        tone: 'info',
        text:
          `Tallene er hentet på ny. Skærmen viser dem inden for ${REFRESH_REACH_SECONDS} sekunder.` +
          /* Only what is saved reaches the screen - the zoom being tried is still a draft. */
          (dirty ? ' Ændringen i Visning er ikke gemt og kommer ikke med.' : '') +
          (result.stale
            ? ' Én eller flere kilder svarede ikke og viser deres seneste tal — se den gule prik.'
            : ''),
      });
    },
    onError: (cause) =>
      setMessage({
        tone: 'error',
        text: cause instanceof ApiError ? cause.message : 'Kunne ikke opdatere skærmen.',
      }),
  });

  return (
    <>
      <PageHeader
        title="TV-dashboard"
        description={
          'Hvordan storskærmene på /dashboard/tv ser ud, og hvad de måler mod. En gemt ændring ' +
          'når skærmene ved deres næste opdatering, senest efter fem minutter' +
          (canEdit ? ' — eller med det samme med "Opdater skærmen nu".' : '.')
        }
        actions={
          canEdit ? (
            <Button
              size="sm"
              variant="secondary"
              loading={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              {refresh.isPending ? null : <RefreshCw className="h-3.5 w-3.5" aria-hidden />}
              Opdater skærmen nu
            </Button>
          ) : undefined
        }
      />

      {message ? (
        <Alert tone={message.tone} className="mb-4">
          {message.text}
        </Alert>
      ) : null}

      {settings.error ? (
        <Alert tone="error" className="mb-4">
          Kunne ikke hente TV-dashboardets indstillinger.
        </Alert>
      ) : null}

      <div className="space-y-4">
        <CollapsibleCard title="Visning">
          <CardBody className="space-y-5">
            {settings.isLoading ? (
              <Skeleton className="h-28" />
            ) : (
              <fieldset disabled={!canEdit} className="m-0 min-w-0 space-y-5 border-0 p-0">
                <div className="space-y-2">
                  <Label htmlFor="tv-zoom">Zoom</Label>
                  <div className="flex items-center gap-3">
                    <Button
                      size="icon"
                      variant="secondary"
                      aria-label="Zoom ud"
                      disabled={current.zoom_pct <= TV_ZOOM_MIN_PCT}
                      onClick={() => edit({ zoom_pct: clamp(current.zoom_pct - TV_ZOOM_STEP_PCT) })}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <input
                      id="tv-zoom"
                      type="range"
                      className="h-2 w-full max-w-md cursor-pointer accent-brand"
                      min={TV_ZOOM_MIN_PCT}
                      max={TV_ZOOM_MAX_PCT}
                      step={TV_ZOOM_STEP_PCT}
                      value={current.zoom_pct}
                      onChange={(event) => edit({ zoom_pct: Number(event.target.value) })}
                    />
                    <Button
                      size="icon"
                      variant="secondary"
                      aria-label="Zoom ind"
                      disabled={current.zoom_pct >= TV_ZOOM_MAX_PCT}
                      onClick={() => edit({ zoom_pct: clamp(current.zoom_pct + TV_ZOOM_STEP_PCT) })}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <span className="tabular w-14 text-right text-sm font-semibold">
                      {formatPercent(current.zoom_pct)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={current.zoom_pct === TV_ZOOM_DEFAULT_PCT}
                      onClick={() => edit({ zoom_pct: TV_ZOOM_DEFAULT_PCT })}
                    >
                      Nulstil til {formatPercent(TV_ZOOM_DEFAULT_PCT)}
                    </Button>
                  </div>
                  <p className="text-xs text-ink-muted">
                    {formatPercent(TV_ZOOM_DEFAULT_PCT)} fylder hele skærmen. Er der for tæt til
                    kanterne på TV&apos;et, så zoom ud, fx til {formatPercent(95)}: hele billedet
                    bliver mindre og får en kant rundt om. Over {formatPercent(TV_ZOOM_DEFAULT_PCT)}{' '}
                    bliver det større, og kanterne skæres af. Gemt nu:{' '}
                    {formatPercent(saved.zoom_pct)}.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tv-autoscroll">Autoscroll i faktureringsgrad pr. person</Label>
                  <Select
                    id="tv-autoscroll"
                    className="w-48"
                    value={current.autoscroll}
                    onChange={(event) => edit({ autoscroll: event.target.value as TvAutoscroll })}
                  >
                    {TV_AUTOSCROLL_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {TV_AUTOSCROLL_LABELS[option]}
                      </option>
                    ))}
                  </Select>
                  <p className="text-xs text-ink-muted">
                    Er der flere personer, end panelet har plads til, ruller listen langsomt nedad
                    og begynder forfra. Fra: rækkerne deles om pladsen, hvor tæt det end bliver.
                    Gemt nu: {TV_AUTOSCROLL_LABELS[saved.autoscroll].toLowerCase()}.
                  </p>
                </div>

                {canEdit ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      disabled={!dirty}
                      loading={save.isPending}
                      onClick={() => save.mutate(current)}
                    >
                      Gem
                    </Button>
                    <Button variant="ghost" disabled={!dirty} onClick={() => setDraft(null)}>
                      Fortryd
                    </Button>
                  </div>
                ) : null}
              </fieldset>
            )}
          </CardBody>
        </CollapsibleCard>

        <TvWelcomeSettings />

        <TvSellersSettings />

        <TvUtilizationSettings />

        <CollapsibleCard
          title="Forhåndsvisning"
          description={
            <>
              {dirty
                ? `Ved ${formatPercent(current.zoom_pct)} og autoscroll ${TV_AUTOSCROLL_LABELS[current.autoscroll].toLowerCase()} - ikke gemt endnu.`
                : `Sådan ser skærmen ud nu, ved ${formatPercent(current.zoom_pct)}.`}{' '}
              Valget her skifter kun forhåndsvisningen - hvert TV viser sin egen skærm.
            </>
          }
          actions={
            <Select
              aria-label="Skærm"
              className="w-64"
              value={screen}
              onChange={(event) => setScreen(event.target.value as TvScreen)}
            >
              {TV_SCREENS.map((option) => (
                <option key={option} value={option}>
                  {TV_SCREEN_INFO[option].label}
                </option>
              ))}
            </Select>
          }
        >
          <CardBody className="space-y-3">
            {screen === 'growth' ? (
              <TvPreview
                ready={Boolean(preview.data)}
                failed={preview.isError}
                zoomPct={current.zoom_pct}
              >
                {preview.data ? (
                  <TvDashboardView
                    data={preview.data}
                    stale={preview.data.stale}
                    autoscroll={current.autoscroll}
                  />
                ) : null}
              </TvPreview>
            ) : screen === 'projects' ? (
              <TvPreview
                ready={Boolean(projectsPreview.data)}
                failed={projectsPreview.isError}
                zoomPct={current.zoom_pct}
                className="bg-tv2-bg"
              >
                {projectsPreview.data ? (
                  <TvProjectsView data={projectsPreview.data} stale={projectsPreview.data.stale} />
                ) : null}
              </TvPreview>
            ) : (
              <TvPreview
                ready={Boolean(internalPreview.data)}
                failed={internalPreview.isError}
                zoomPct={current.zoom_pct}
                className="bg-tv2-bg"
              >
                {internalPreview.data ? (
                  <TvInternalView data={internalPreview.data} stale={internalPreview.data.stale} />
                ) : null}
              </TvPreview>
            )}
            {canEdit ? (
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  size="sm"
                  variant="secondary"
                  loading={copyUrl.isPending}
                  onClick={() => copyUrl.mutate(screen)}
                >
                  {copyUrl.isPending ? null : <Copy className="h-3.5 w-3.5" aria-hidden />}
                  Kopier URL
                </Button>
                <span className="text-xs text-ink-muted">
                  {TV_SCREEN_INFO[screen].path}?token=… — tokenet er{' '}
                  {TV_SCREEN_INFO[screen].token_setting} på /admin/integrationer.
                </span>
              </div>
            ) : null}
          </CardBody>
        </CollapsibleCard>
      </div>
    </>
  );
}
